import { useState } from "react";
import { X, Crown, Check, Mail, Loader2 } from "lucide-react";
import { addEarlyAccess } from "../lib/storage";
import { useProState } from "../lib/pro";
import { toast } from "sonner";
import { isPlayBillingAvailable, buyProSubscription } from "../lib/playBilling";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRO_FEATURES = [
  "Unlimited saved events",
  "Event comparison view",
  "Post-event report & actuals tracking",
  "Vendor insights dashboard",
  "Printable booth checklist",
  "Full event history",
];

export default function UpgradeModal() {
  const { upgradeOpen, closeUpgrade, saveEmail } = useProState();
  const { refresh } = useProState();
  const [email, setEmailValue] = useState("");
  const [loading, setLoading] = useState(false);

  if (!upgradeOpen) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/.+@.+\..+/.test(trimmed)) {
      toast.error("Please enter a valid email");
      return;
    }

    setLoading(true);
    try {
      // Save email locally so we can check status on return
      saveEmail(trimmed);
      addEarlyAccess(trimmed); // preserve early-access list as well
      if (isPlayBillingAvailable()) { await buyProSubscription({ email: trimmed }); await refresh(trimmed); toast.success("You're Pro! Welcome to PopUp Profit Pro."); closeUpgrade(); setLoading(false); return; }

      const res = await fetch(`${API}/checkout/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          origin_url: window.location.origin,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Could not start checkout");
      }
      const data = await res.json();
      if (!data.url) throw new Error("No checkout URL");
      // Redirect to Stripe-hosted Checkout
      window.location.href = data.url;
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Something went wrong starting checkout");
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-brand-ink/50 backdrop-blur-sm p-0 sm:p-4 animate-fade-up"
      data-testid="upgrade-modal"
      onClick={closeUpgrade}
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto shadow-2xl border border-brand-line"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative p-6 sm:p-8">
          <button
            onClick={closeUpgrade}
            className="absolute top-4 right-4 w-9 h-9 grid place-items-center rounded-full hover:bg-brand-cream-soft text-brand-mute"
            data-testid="upgrade-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="inline-flex items-center gap-1.5 pop-pill bg-brand-gold/15 text-brand-gold border border-brand-gold/30">
            <Crown className="w-3.5 h-3.5" strokeWidth={2.5} />
            PopUp Profit Pro
          </div>

          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-ink mt-4 tracking-tight">
            Unlock everything.<br />
            <span className="text-brand-green">Know before you show.</span>
          </h2>

          <p className="text-sm text-brand-mute mt-3 leading-relaxed">
            Unlimited events, post-event reports, event comparison, printable checklists, and full vendor insights —
            for one simple monthly subscription. Cancel anytime in your Stripe account.
          </p>

          <div className="mt-5 pop-card p-4 bg-brand-cream-soft border-brand-line">
            <div className="flex items-baseline gap-1">
              <span className="font-heading text-3xl font-semibold text-brand-ink">$9</span>
              <span className="text-sm text-brand-mute">/ month</span>
            </div>
            <p className="text-xs text-brand-mute mt-1">Secure recurring checkout via Stripe. Cancel anytime.</p>
          </div>

          <ul className="mt-5 space-y-2.5">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-brand-ink">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-green/10 text-brand-green grid place-items-center shrink-0">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
                {f}
              </li>
            ))}
          </ul>

          <form onSubmit={onSubmit} className="mt-6" data-testid="upgrade-form" noValidate>
            <label className="pop-label" htmlFor="upgrade-email">Email to unlock Pro</label>
            <div className="relative mt-1">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mute" />
              <input
                id="upgrade-email"
                type="email"
                inputMode="email"
                placeholder="you@vendor.shop"
                value={email}
                onChange={(e) => setEmailValue(e.target.value)}
                className="pop-input pl-10"
                data-testid="upgrade-email-input"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              className="pop-btn-primary w-full mt-3 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="upgrade-submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Stripe…
                </>
              ) : (
                <>
                  <Crown className="w-4 h-4" /> Subscribe — $9/month
                </>
              )}
            </button>
            <p className="text-[11px] text-brand-mute mt-2 text-center">
              We use this email to grant your Pro access. Recurring monthly billing via Stripe — cancel anytime.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
